import { useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { resolveRichText } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  EditableToken,
  RichTextContent,
  useContentToken,
  useProperty,
} from '@vibesites/runtime/react';
import { keepLocationTogether, keepPropertyNameTogether, stripWrappingParagraph } from '../utils/keepLocationTogether';
import {
  INNER_PAGE_HERO_SUBTITLE_CLASS,
  INNER_PAGE_HERO_TITLE_CLASS,
  InnerPageHeroSection,
} from './shared/InnerPageHeroSection';

const bgImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: '3 bedroom floor plans banner',
    },
  },
};

export function ThreeBedroomsHero() {
  const property = useProperty();

  const subtitleRaw = useContentToken('threebedroomshero.subtitle');
  const subtitleResolved = useMemo(() => {
    if (!subtitleRaw) return '';
    let html = resolveRichText(subtitleRaw, property as Record<string, any>);
    html = stripWrappingParagraph(html);
    html = keepPropertyNameTogether(html, property.property_name as string | null | undefined);
    return keepLocationTogether(
      html,
      property.property_address_city as string | null | undefined,
      property.property_address_state as string | null | undefined,
    );
  }, [subtitleRaw, property]);

  return (
    <InnerPageHeroSection
      bgGroupPath="threebedroomshero.bg_image"
      bgImageDef={bgImageDef}
      altFallback="3 bedroom floor plans banner"
    >
      <EditableToken
        path="threebedroomshero.subtitle"
        type="richtext"
        as="h2"
        className={INNER_PAGE_HERO_SUBTITLE_CLASS}
      >
        <span
          data-vibesite-richtext=""
          dangerouslySetInnerHTML={{ __html: subtitleResolved }}
          style={{ display: 'contents' }}
        />
      </EditableToken>
      <RichTextContent
        path="threebedroomshero.title"
        as="h1"
        className={INNER_PAGE_HERO_TITLE_CLASS}
      />
      <EditableSlotGroup
        groupPath="threebedroomshero.bg_image"
        definition={bgImageDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
    </InnerPageHeroSection>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ThreeBedroomsHero',
  displayName: '3 Bedrooms Hero',
  tokens: {
    'threebedroomshero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>3 Bedrooms</p>',
    },
    'threebedroomshero.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue:
        '<p>3-Bedroom Apartments in <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span></p>',
      description:
        'Short, SEO-style headline specific to 3-bedroom floor plans (e.g. "3-Bedroom Apartments in Tigard, OR").',
    },
  },
  slotGroups: {
    'threebedroomshero.bg_image': bgImageDef,
  },
};

(ThreeBedroomsHero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ThreeBedroomsHero;
